import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { forwardRef } from "react";

/**
 * ScaleOnHover - Interactive wrapper that scales on hover and tap
 * 
 * Adds subtle scale animations to interactive elements like buttons,
 * avatars, and cards. Provides visual feedback for user interactions.
 * 
 * @component
 * @example
 * ```jsx
 * import { ScaleOnHover } from "../ui/Motion";
 * 
 * // Basic usage
 * <ScaleOnHover onClick={handleClick}>
 *   <div className="card">Click me</div>
 * </ScaleOnHover>
 * 
 * // Custom scale values
 * <ScaleOnHover hoverScale={1.1} tapScale={0.9}>
 *   <button>Big reaction</button>
 * </ScaleOnHover>
 * 
 * // With className
 * <ScaleOnHover className="cursor-pointer">
 *   <img src="avatar.jpg" alt="Avatar" />
 * </ScaleOnHover>
 * ```
 */
export const ScaleOnHover = forwardRef(({
    children,
    hoverScale = 1.05,
    tapScale = 0.95,
    className,
    style,
    onClick,
    ...props
}, ref) => (
    <motion.div
        ref={ref}
        whileHover={{ scale: hoverScale }}
        whileTap={{ scale: tapScale }}
        className={className}
        style={style}
        onClick={onClick}
        {...props}
    >
        {children}
    </motion.div>
));

ScaleOnHover.displayName = "ScaleOnHover";

ScaleOnHover.propTypes = {
    children: PropTypes.node.isRequired,
    hoverScale: PropTypes.number,
    tapScale: PropTypes.number,
    className: PropTypes.string,
    style: PropTypes.object,
    onClick: PropTypes.func
};

export default ScaleOnHover;
